import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMatch } from "react-router";
import type { CorpusSummary } from "../../../api/content/corporaApi";
import { appPaths, ROUTE_PATTERNS } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useProjects } from "../../../projects/projectContext";
import { parseDocumentAddress, type DocumentVersionRef } from "../../../shared/references/contentReferences";
import AppButton from "../../../ui/buttons/AppButton";
import RenderInSlot from "../../../ui/layout/RenderInSlot";
import type { DocumentOrder } from "../../../api/content/sourcesApi";
import { CorpusSearch, NO_SEARCH_FILTERS, narrowingCount, type SearchFilters } from "../../search";
import { useCorpusSelection } from "../corpus/corpusSelectionContext";
import ShownCorporaChoice from "../corpus/ShownCorporaChoice";
import { useSourceDocument } from "../document/useSourceDocument";
import { useImportPreparation } from "../import/useImportPreparation";
import CorpusCard from "./CorpusCard";
import DocumentList from "./DocumentList";
import DocumentNavigationControls from "./DocumentNavigationControls";
import { useDocumentList } from "./useDocumentList";
import { useDocumentNavigation } from "./useDocumentNavigation";
import LoadStatus from "../../../ui/feedback/LoadStatus";

const NO_CORPORA: readonly CorpusSummary[] = [];

/**
 * The Documents sidebar: previous, random and next at the very top (F41), DMW's one search in one
 * field for keys, references and text (F135) over the shown corpora, the corpora it lists, and per
 * shown corpus its compact card (F137) and document list. Hidden
 * corpora are not loaded (F20). The buttons browse the corpus worked in, or the first shown one; that
 * corpus's section renders them into the top slot, so each section keeps its own list. The search's
 * filters also filter the lists, its kind of segment included (F141), and each document shows its
 * most developed form (F88) and its key in the form chosen (F136).
 */
export default function SourcesSidebar() {
  const { t } = useTranslation();
  const projects = useProjects();
  const { corpora, selectedCorpus, hiddenCorpusIds, reloadCorpora } = useCorpusSelection();
  const loaded = corpora?.status === "loaded" ? corpora.value : NO_CORPORA;
  const shown = loaded.filter((corpus) => !hiddenCorpusIds.has(corpus.corpusId));
  const startImport = useImportPreparation();
  const [navigationSlot, setNavigationSlot] = useState<HTMLDivElement | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(NO_SEARCH_FILTERS);
  const [order, setOrder] = useState<DocumentOrder>("creation");
  // While the field holds a query, the lists step aside for its results ("user focus"); hidden,
  // not unmounted, so they keep their place and previous, random and next stay at the top.
  const [query, setQuery] = useState("");
  const searching = query.trim() !== "";
  // The open document's corpus, as its read names it (E11): however it was opened, and wherever
  // it lies in its list. This pane's own, so the split view's two panes never fight over the
  // stored choice. The read is the document page's, through the session's document cache.
  const opened = useSourceDocument(useOpenDocumentVersion()).state;
  const openCorpusId = opened.status === "loaded" ? (opened.value?.corpusId ?? null) : null;
  const browsed =
    shown.find((corpus) => corpus.corpusId === openCorpusId) ??
    shown.find((corpus) => corpus.corpusId === selectedCorpus?.corpusId) ??
    shown[0] ??
    null;

  return (
    <div className="space-y-4 px-3 py-4">
      <div ref={setNavigationSlot} className="space-y-2 empty:hidden" />
      {/* Without a project the page itself says what to do; nothing to open or search yet (TR-A2-03). */}
      {projects.selectedProjectId === null ? null : (
        <CorpusSearch
          projectId={projects.selectedProjectId}
          corpora={{ list: loaded, excluded: hiddenCorpusIds }}
          filters={{ value: filters, onChange: setFilters }}
          query={{ value: query, onChange: setQuery }}
          // F140: a result opens beside, as a new tab there, so the results stay in view.
          hitAction={{ kind: "open", routeOf: (address) => appPaths.sourceDocument(address.document, address.segmentPath), placement: "beside" }}
        />
      )}
      {corpora === null ? null : <LoadStatus state={corpora} loadingLabel={t("common.loading")} onRetry={reloadCorpora} compact />}
      {corpora?.status === "loaded" && selectedCorpus === null ? (
        <p className="app-text-muted px-1 text-xs">{t("sources.sidebar.noCorpus")}</p>
      ) : null}
      {startImport === null ? null : (
        <AppButton variant="secondary" size="sm" fullWidth className="gap-2" onClick={startImport}>
          <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
          {t("sources.import.button")}
        </AppButton>
      )}
      <ShownCorporaChoice corpora={loaded} />
      {loaded.length > 0 && shown.length === 0 ? <p className="app-text-muted px-1 text-xs">{t("sources.corpus.allHidden")}</p> : null}
      <div className="space-y-4" hidden={searching}>
        {shown.map((corpus) => (
          <CorpusDocuments
            key={`${corpus.corpusId}@${corpus.latestVersion.commit}`}
            corpus={corpus}
            navigationSlot={corpus.corpusId === browsed?.corpusId ? navigationSlot : null}
            listed={{ filters, order, onOrderChange: setOrder }}
          />
        ))}
      </div>
    </div>
  );
}

/** The document this pane shows, from its address. */
function useSelectedDocumentId(): string | null {
  const documentMatch = useMatch(ROUTE_PATTERNS.sourceDocument);
  return parseDocumentAddress(documentMatch?.params.documentAddress)?.documentId ?? null;
}

/** The exact document version this pane shows; `null` until the page has resolved "latest". */
function useOpenDocumentVersion(): DocumentVersionRef | null {
  const documentMatch = useMatch(ROUTE_PATTERNS.sourceDocument);
  const address = parseDocumentAddress(documentMatch?.params.documentAddress);
  const documentId = address?.documentId ?? null;
  const version = address?.version ?? null;
  return useMemo(() => (documentId === null || version === null ? null : { documentId, version }), [documentId, version]);
}

interface CorpusDocumentsProps {
  corpus: CorpusSummary;
  /** Where this corpus's previous, random and next go while it is the one browsed; else `null`. */
  navigationSlot: HTMLElement | null;
  /** Which documents the list keeps, and their order: one choice for every corpus listed. */
  listed: { readonly filters: SearchFilters; readonly order: DocumentOrder; readonly onOrderChange: (order: DocumentOrder) => void };
}

/** One corpus's documents; using them makes it the corpus worked in (F41). */
function CorpusDocuments({ corpus, navigationSlot, listed }: CorpusDocumentsProps) {
  const { t } = useTranslation();
  const { selectCorpus } = useCorpusSelection();
  const selectedDocumentId = useSelectedDocumentId();
  const list = useDocumentList(corpus.latestVersion, listed.filters, listed.order);
  const navigation = useDocumentNavigation(corpus.latestVersion, list.totalDocumentCount, list.documents, selectedDocumentId, listed.filters);
  const listAvailable = list.firstPage.status === "loaded";

  return (
    <section className="space-y-2" aria-label={corpus.name} onClickCapture={() => { selectCorpus(corpus.corpusId); }}>
      <CorpusCard corpus={corpus} documentCount={list.totalDocumentCount} order={listed.order} onOrderChange={listed.onOrderChange} />
      <RenderInSlot slot={navigationSlot}>
        <DocumentNavigationControls navigation={navigation} enabled={listAvailable && list.totalDocumentCount > 0} />
        {navigation.navigationError ? <ErrorNotice error={navigation.navigationError} compact={true} /> : null}
      </RenderInSlot>
      <nav aria-label={t("sources.list.title")}>
        <LoadStatus state={list.firstPage} loadingLabel={t("common.loading")} onRetry={list.reload} compact />
        {listAvailable ? (
          <DocumentList
            filtered={narrowingCount(listed.filters.attached) > 0}
            documents={list.documents}
            selectedDocumentId={selectedDocumentId}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
          />
        ) : null}
        {list.loadMoreError ? <ErrorNotice error={list.loadMoreError} onRetry={list.loadMore} compact={true} /> : null}
      </nav>
    </section>
  );
}
